import { z } from "zod";
import { ExpectedError } from "../../expectedError";
import { oauthSetupSteps } from "../oauthSetupSteps";
import { customAuth, queryAuth } from "../standardAuthPatterns";
import { defineProduct, productVariant } from "../types";
import logo from "./logo.svg";

const refreshContextSchema = z.object({
  refresh_token: z.string(),
  device_id: z.string(),
});

function refreshContext(value: string | undefined) {
  let parsed: unknown;
  try {
    parsed = JSON.parse(value ?? "");
  } catch {
    throw new ExpectedError("VK refresh credentials are invalid");
  }
  const context = refreshContextSchema.safeParse(parsed);
  if (!context.success)
    throw new ExpectedError("VK refresh credentials are invalid");
  return context.data;
}

function normalizeVkToken(
  body: Record<string, unknown>,
  request: Record<string, string> = {}
) {
  if (body.error || typeof body.access_token !== "string")
    throw new ExpectedError("VK rejected the token request");
  const deviceId =
    request.device_id ??
    (request.refresh_token
      ? refreshContext(request.refresh_token).device_id
      : undefined);
  if (!deviceId || !request.client_id)
    throw new ExpectedError("VK token context is missing");
  return {
    ...body,
    token_type: "Bearer",
    access_token: JSON.stringify({
      access_token: body.access_token,
      client_id: request.client_id,
    }),
    ...(typeof body.refresh_token === "string"
      ? {
          refresh_token: JSON.stringify({
            refresh_token: body.refresh_token,
            device_id: deviceId,
          }),
        }
      : {}),
  };
}

export const vk = defineProduct({
  name: "VK",
  tagLine: "VK ID profiles, communities, and messaging",
  logo,
  tags: ["communication"],
  variants: [
    productVariant({
      identifier: "vk",
      authLabel: "VK ID OAuth",
      protocol: "http",
      defaultUrlPattern: () =>
        "https://id\\.vk\\.ru/oauth2/(?:user_info|public_info)(?:\\?.*)?$",
      auth: customAuth("vk"),
      steps: oauthSetupSteps({
        identifier: "vk",
        registrationUrl: "https://id.vk.com/about/business/",
        scopes: ["email"],
        publicClient: true,
        instructions:
          "Register a web application in VK ID and configure its redirect URL. VK ID uses PKCE and a device ID to authorize and renew access. This option accesses VK ID profiles; use an API token for VK community and messaging APIs.",
      }),
      oauth: {
        grantType: () => "authorization_code",
        authorizationEndpoint: () => "https://id.vk.ru/authorize",
        tokenEndpoint: () => "https://id.vk.ru/oauth2/auth",
        tokenEndpointAuthMethod: () => "none",
        tokenRequestIncludesState: true,
        tokenRequestCallbackParameters: ["device_id"],
        clientCredentialsFrom: "db",
        scopes: () => ["email"],
        pkce: () => true,
        normalizeTokenRequest: (parameters) => {
          if (parameters.grant_type !== "refresh_token") return parameters;
          const context = refreshContext(parameters.refresh_token);
          return {
            ...parameters,
            refresh_token: context.refresh_token,
            device_id: context.device_id,
            state: crypto.randomUUID(),
          };
        },
        normalizeTokenResponse: normalizeVkToken,
      },
      connectionTest: {
        protocol: "http",
        request: () => ({
          url: "https://id.vk.ru/oauth2/user_info",
          method: "POST",
          expectedBodyContains: ['"user"', '"user_id"'],
        }),
      },
      usageHint: () =>
        "Call https://id.vk.ru/oauth2/user_info or /oauth2/public_info with POST. The proxy supplies access_token and client_id as form parameters. VK ID tokens are separate from VK API user, community, and service tokens.",
    }),
    productVariant({
      identifier: "vkApiToken",
      authLabel: "API token (user, community, or service)",
      protocol: "http",
      defaultUrlPattern: () => "https://api\\.vk\\.(?:ru|com)/method/.*",
      auth: (app) =>
        queryAuth([{ name: "access_token", value: app.secret.accessToken }]),
      steps: [
        {
          title: "Create a VK API token",
          items: [
            {
              kind: "markdown",
              content:
                "Create a user, community, or service token using the [VK API access token documentation](https://dev.vk.com/ru/api/access-token/getting-started). Choose permissions for your workflow. Community messaging requires a community token with messages access; user and service tokens have different permissions. Replace expiring tokens before they expire.",
            },
            {
              kind: "input",
              label: "Access token",
              required: true,
              target: { kind: "secrets", field: "accessToken" },
            },
          ],
        },
      ],
      connectionTest: {
        protocol: "http",
        request: () => ({
          url: "https://api.vk.ru/method/users.get?user_ids=1&v=5.199",
          expectedBodyContains: '"response"',
        }),
      },
      usageHint: () =>
        "Use https://api.vk.ru/method/ with v=5.199. The proxy supplies access_token. The saved token’s type and permissions determine which user, community, and messaging operations are available.",
    }),
  ],
});
